import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Fieldset, Heading, Paragraph, Radio, Tag } from '@digdir/designsystemet-react';
import type { AreaDecisionType, PlanArea } from '../../../api/workEnvironmentTypes';
import { useDecideAreaMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { TextField } from '../components/FormParts';
import { areasToDecide, decisionId, decisionNeedsReason, decisionProgress } from '../planView';
import { areaName, LEVEL_COLORS } from '../riskView';
import { dayAndMonth } from '../surveyProcessView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = {
  surveyId: number;
  areas: PlanArea[];
  canChange: boolean;
  /** The area the page was opened at to register a decision */
  focus?: string | null;
};

type FormProps = { surveyId: number; area: PlanArea; canChange: boolean; focused: boolean };

const DecisionForm = ({ surveyId, area, canChange, focused }: FormProps) => {
  const { t, i18n } = useTranslation();
  const [decision, setDecision] = useState<AreaDecisionType | null>(area.decision?.decision ?? null);
  const [reason, setReason] = useState(area.decision?.reason ?? '');
  const [missingReason, setMissingReason] = useState(false);
  const decide = useDecideAreaMutation(surveyId);
  const name = areaName(area, t);

  const save = () => {
    if (!decision) return;
    const needsReason = decisionNeedsReason(decision) && !reason.trim();
    setMissingReason(needsReason);
    if (!needsReason) decide.mutate({ factorKey: area.key, decision, reason });
  };

  return (
    <li id={decisionId(area.key)} className={`${classes.dataArea} ${focused ? process.focused : ''}`}>
      <div className={classes.areaSummary}>
        <span className="ds-label">{name}</span>
        <span className={classes.areaTags}>
          <Tag data-size="sm" data-color={LEVEL_COLORS[area.level]}>
            {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
          </Tag>
          <Tag data-size="sm" data-color={area.decision ? 'success' : 'warning'}>
            {t(area.decision ? 'workEnvironmentPlan.decisions.decided' : 'workEnvironmentPlan.decisions.missing')}
          </Tag>
          {area.deadline && (
            <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
              {t('workEnvironmentPlan.decisions.deadline', { date: dayAndMonth(area.deadline, i18n.language) })}
            </span>
          )}
        </span>
      </div>
      {canChange ? (
        <>
          <Fieldset data-size="sm">
            <Fieldset.Legend className="ds-sr-only">
              {t('workEnvironmentPlan.decisions.legend', { name })}
            </Fieldset.Legend>
            {area.decisions.map((option) => (
              <Radio
                key={option}
                name={`decision-${area.key}`}
                value={option}
                label={t(`workEnvironmentPlan.decisions.options.${option}`)}
                checked={decision === option}
                onChange={() => setDecision(option)}
              />
            ))}
          </Fieldset>
          {decisionNeedsReason(decision) && (
            <TextField
              id={`decision-${area.key}-reason`}
              label={t('workEnvironmentPlan.decisions.reason')}
              value={reason}
              error={missingReason ? t('workEnvironmentPlan.decisions.reasonError') : undefined}
              onChange={setReason}
            />
          )}
          {decide.isError && (
            <Alert data-color="danger">
              <Paragraph>{t('workEnvironmentPlan.decisions.failed')}</Paragraph>
            </Alert>
          )}
          {decision && (
            <div>
              <Button variant="secondary" data-size="sm" loading={decide.isPending} onClick={save}>
                {t('workEnvironmentPlan.decisions.save')}
              </Button>
            </div>
          )}
        </>
      ) : (
        area.decision && (
          <Paragraph data-size="sm">
            {t(`workEnvironmentPlan.decisions.options.${area.decision.decision}`)}
            {area.decision.reason && `: ${area.decision.reason}`}
          </Paragraph>
        )
      )}
    </li>
  );
};

/** Every area with moderate or high risk needs a plan or a decision before HR's deadline */
export const DecisionsCard = ({ surveyId, areas, canChange, focus = null }: Props): ReactElement | null => {
  const { t } = useTranslation();
  const withoutPlan = areasToDecide(areas);
  const progress = decisionProgress(areas);
  if (progress.total === 0) return null;

  return (
    <section aria-labelledby="plan-decisions-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="plan-decisions-heading">
            {t('workEnvironmentPlan.decisions.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentPlan.decisions.intro')}
          </Paragraph>
        </div>
        <Paragraph data-size="sm">
          {t('workEnvironmentPlan.decisions.progress', { done: progress.done, total: progress.total })}
        </Paragraph>
        <ul className={classes.dataAreas}>
          {withoutPlan.map((area) => (
            <DecisionForm
              key={area.key}
              surveyId={surveyId}
              area={area}
              canChange={canChange}
              focused={area.key === focus}
            />
          ))}
        </ul>
      </div>
    </section>
  );
};
